/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import styled from "styled-components";
import { DecimalInput, DropDown2 } from "components";
import { useInput } from "hooks";
import {
  REWARDS_AND_COUPONS_TEXT_OPTIONS,
  REWARDS_AND_COUPONS_VALUE_TYPE_OPTIONS,
} from "config";

const formatRewardsCouponsText = (valueType, amount, offerOption) => {
  if (!offerOption) return "w/";
  if (!amount) return `w/${offerOption}`;
  const prefix = valueType === "$" ? "$" : "";
  const suffix = valueType === "unit" ? " unit" : "";
  return `w/${prefix}${amount}${suffix} ${offerOption}`;
};

const parseRewardsCouponsText = (value = "") => {
  const raw = value.trim();
  if (!raw.toLowerCase().startsWith("w/")) {
    return { valueType: "", amount: "", offerOption: "coupon" };
  }
  const content = raw.slice(2).trim();
  const matchedOption = [...REWARDS_AND_COUPONS_TEXT_OPTIONS]
    .sort((a, b) => b.title.length - a.title.length)
    .find((opt) => content.toLowerCase().endsWith(opt.title.toLowerCase()));

  if (!matchedOption) {
    return { valueType: "", amount: "", offerOption: content || "coupon" };
  }

  const prefix = content.slice(0, content.length - matchedOption.title.length).trim();
  if (!prefix) {
    return { valueType: "", amount: "", offerOption: matchedOption.id };
  }
  const floatRegex = "(\\d+(?:\\.\\d*)?|\\.\\d*)";
  if (new RegExp(`^${floatRegex}\\s+unit$`, "i").test(prefix)) {
    return { valueType: "unit", amount: prefix.match(new RegExp(`^${floatRegex}`))[1], offerOption: matchedOption.id };
  }
  if (new RegExp(`^\\$${floatRegex}$`).test(prefix)) {
    return { valueType: "$", amount: prefix.slice(1), offerOption: matchedOption.id };
  }
  if (new RegExp(`^${floatRegex}$`).test(prefix)) {
    return { valueType: "", amount: prefix, offerOption: matchedOption.id };
  }
  return { valueType: "", amount: "", offerOption: matchedOption.id };
};

export const OfferRewardsCouponsTextBuilder = ({
  className,
  value,
  onChange = () => {},
}) => {
  const valueType = useInput("");
  const amount = useInput("");
  const offerOption = useInput("coupon");

  const handleUpdate = (vt, amt, opt) => {
    onChange(formatRewardsCouponsText(vt, amt, opt));
  };

  useEffect(() => {
    const parsed = parseRewardsCouponsText(value || "w/coupon");
    valueType.setValue(parsed.valueType);
    amount.setValue(parsed.amount);
    offerOption.setValue(parsed.offerOption);
    return () => {};
  }, [value]);

  return (
    <div className={`${className} mt-1 py-2`}>
      <Title>Offer Text Builder</Title>
      <TopRow className="mt-2">
        <Prefix>w/</Prefix>
        <TypeField>
          <DropDown2
            label="Type"
            width={85}
            data={REWARDS_AND_COUPONS_VALUE_TYPE_OPTIONS}
            value={valueType.value}
            onChange={(v) => {
              valueType.setValue(v);
              handleUpdate(v, amount.value, offerOption.value);
            }}
          />
        </TypeField>
        <NumberField>
          <DecimalInput
            label="Value"
            prefix=""
            value={amount.value}
            onChange={(v) => {
              amount.setValue(v);
              handleUpdate(valueType.value, v, offerOption.value);
            }}
          />
        </NumberField>
      </TopRow>
      <BottomRow className="mt-1">
        <DropDown2
          label="Coupon / Reward"
          width={100}
          data={REWARDS_AND_COUPONS_TEXT_OPTIONS}
          value={offerOption.value}
          onChange={(v) => {
            offerOption.setValue(v);
            handleUpdate(valueType.value, amount.value, v);
          }}
        />
      </BottomRow>
    </div>
  );
};

const Title = styled.div`
  color: #66c0e1;
  font-size: 16px;
`;

const TopRow = styled.div`
  background: ${(props) => props.theme.palette.items};
  padding: 8px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  box-sizing: border-box;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const BottomRow = styled.div`
  background: ${(props) => props.theme.palette.items};
  padding: 8px;
  box-sizing: border-box;
  width: 100%;
`;

const Prefix = styled.div`
  font-size: 14px;
  color: white;
  padding-bottom: 6px;
  flex-shrink: 0;
`;

const TypeField = styled.div`
  flex-shrink: 0;
  width: 90px;
`;

const NumberField = styled.div`
  flex: 1;
  min-width: 0;
`;
